import { TableDashboardItemHelper } from 'src/app/components/table-dashboard-item/table-dashboard-item-helper';
import {
    BITableOptions,
    ITableDashboardItemComponent,
    TableColumnItem,
    TableDataItem,
} from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import { FlowBreakdownRow } from 'src/app/services/flow-dashboard/flow-dashboard.models';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';

export interface FlowBreakdownTableOptions {
    dashboardName: string;
    instanceId: string;
    title: string;
    height?: string;
    nameColumnLabel?: string;
}

/** Reusable tree table whose rows mirror and toggle flow-chart legend series. */
export function createFlowBreakdownTableDashboardItem(
    options: FlowBreakdownTableOptions
): ITableDashboardItemComponent {
    const formatValue = (value: number): string =>
        Math.abs(value) >= 1000
            ? EChartsAxisHelper.axisNumberLabelFormatter(value)
            : parseFloat(value.toFixed(2)).toString();
    const columns: TableColumnItem[] = [
        {
            id: 'color',
            name: '',
            nzLeft: true,
            nzAlign: 'left',
            nzWidth: '28px',
            nzSortFn: () => 0,
        },
        {
            id: 'name',
            name: options.nameColumnLabel ?? 'Source',
            nzLeft: true,
            nzAlign: 'left',
            nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                'name',
                'string'
            ),
            treeColumn: true,
            nzWidth: '60%',
        },
        {
            id: 'value',
            name: 'Value',
            nzLeft: false,
            nzAlign: 'right',
            nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                'value',
                'number'
            ),
            nzWidth: '70px',
            labelFormatter: (value: number) => formatValue(value),
        },
        {
            id: 'share',
            name: 'Share',
            nzLeft: false,
            nzAlign: 'right',
            nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                'share',
                'number'
            ),
            nzWidth: '60px',
            labelFormatter: (value: number) => `${(value * 100).toFixed(1)}%`,
        },
    ];
    const composeData = (rawData: FlowBreakdownRow[]): BITableOptions => {
        let index = 0;
        const toItem = (row: FlowBreakdownRow): TableDataItem => ({
            index: index++,
            muted: row.hidden,
            key: row.key,
            meta: { seriesName: row.seriesName },
            data: {
                color: {
                    value: '',
                    label: '',
                    'background-color': row.color,
                },
                name: { value: row.name, label: row.name },
                value: {
                    value: row.value,
                    label: formatValue(row.value),
                },
                share: {
                    value: row.share,
                    label: `${(row.share * 100).toFixed(1)}%`,
                },
            },
            children: row.children?.length
                ? row.children.map(toItem)
                : undefined,
        });
        return {
            columns,
            dataset: { data: (rawData ?? []).map(toItem) },
        };
    };

    return {
        dashboardName: options.dashboardName,
        instanceId: options.instanceId,
        title: options.title,
        viewMode: 'scroll',
        height: options.height ?? '360px',
        component: 'TableDashboardItemComponent',
        composeData,
    };
}
